import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import QRCode from 'qrcode';
import { normalizeRoomCode } from '@shared/rules';
import type { RoomView } from '@shared/types';
import { AnswerCard, FaceDownCard, FilledPrompt, PromptCard } from '../components/Cards';
import { Avatar, Scorebar, Timer, roomLink, useCountdown, useDocumentTitle } from '../components/Chrome';
import { call, getSocket } from '../lib/socket';
import { TeamBar, WorstBanner, championNames, names } from './room/Modes';

/** Big-screen view for a laptop or TV: no hand, just the table. */
export function Tv() {
  const code = normalizeRoomCode(useParams().code ?? '');
  useDocumentTitle(`TV · ${code}`);
  const [view, setView] = useState<RoomView | null>(null);
  const [error, setError] = useState('');
  const [qr, setQr] = useState('');
  const offset = useRef(0);

  useEffect(() => {
    QRCode.toDataURL(roomLink(code), { margin: 1, width: 480, color: { dark: '#1c1530', light: '#ffffff' } })
      .then(setQr)
      .catch(() => {});
    const socket = getSocket();
    const watch = async () => {
      const res = await call('room:watch', { code });
      setError(res.ok ? '' : res.error);
    };
    const onState = (v: RoomView) => {
      if (v.code !== code) return;
      offset.current = v.serverTime - Date.now();
      setView(v);
    };
    socket.on('room:state', onState);
    socket.on('connect', watch);
    if (socket.connected) void watch();
    return () => {
      socket.off('room:state', onState);
      socket.off('connect', watch);
    };
  }, [code]);

  const seconds = useCountdown(view?.round?.deadline ?? null, () => Date.now() + offset.current);

  if (error) {
    return (
      <div className="tv center" style={{ justifyContent: 'center' }}>
        <h1>{error}</h1>
        <Link to="/" className="btn btn-primary btn-lg" style={{ alignSelf: 'center' }}>
          Home
        </Link>
      </div>
    );
  }
  if (!view) return <div className="tv" />;

  const round = view.round;
  const judge = view.players.find((p) => p.id === round?.judgeId);
  const winners = round?.submissions.filter((s) => s.isWinner) ?? [];
  const pending = view.players.filter((p) => p.connected && !p.done && p.id !== round?.judgeId);
  const writer = view.players.find((p) => p.id === round?.writerId);
  const guessWaiting = view.players.filter((p) => p.connected && round?.guessers.includes(p.id) && !p.done);
  const rightGuessers = round?.guesses?.filter((g) => g.correct) ?? [];
  const champions = championNames(view);
  const bigLine = { fontSize: 'clamp(1.1rem, 2vw, 2rem)', fontWeight: 700, margin: 0 } as const;
  let roundStatus = judge ? ` · 👑 ${judge.name} is judging` : ' · 🗳️ Everyone votes';
  if (view.phase === 'choosing') roundStatus = ` · 👑 ${judge?.name ?? 'The judge'} is picking a prompt`;
  if (view.phase === 'writing') roundStatus = ` · ✍️ ${writer?.name ?? 'Someone'} is writing`;
  if (view.phase === 'guessing') roundStatus = ' · 🕵️ Guess who';

  return (
    <div className="tv">
      <div className="tv-top">
        <img src="/logo.png" alt="" />
        <h1 style={{ margin: 0 }} className="grow">
          CompleteMasti
        </h1>
        <span style={{ fontSize: 'clamp(1rem, 2vw, 2rem)', fontWeight: 800 }}>
          {window.location.host}/game/<span style={{ color: 'var(--orange)' }}>{view.code}</span>
        </span>
      </div>

      {view.phase === 'lobby' && (
        <>
          <div className="join-box">
            {qr && <img src={qr} alt={`QR code to join room ${view.code}`} />}
            <div>
              <div style={{ fontSize: 'clamp(1.2rem, 2.4vw, 2.4rem)', fontWeight: 800 }}>Scan to join, or enter the code</div>
              <div className="join-code">{view.code}</div>
              <div className="muted" style={{ fontSize: 'clamp(1rem, 1.6vw, 1.6rem)' }}>
                {view.players.length} {view.players.length === 1 ? 'player' : 'players'} at the table.{' '}
                {view.players.filter((p) => p.connected).length >= view.minPlayers
                  ? `Waiting for ${view.players.find((p) => p.isHost)?.name ?? 'the host'} to start.`
                  : `Need ${view.minPlayers} to start.`}
              </div>
            </div>
          </div>
          <div className="row" style={{ gap: '2vw' }}>
            {view.players.map((p) => (
              <div key={p.id} className="row" style={{ fontSize: 'clamp(1rem, 1.8vw, 1.8rem)', fontWeight: 800 }}>
                <Avatar player={p} size="lg" /> {p.name}
              </div>
            ))}
          </div>
        </>
      )}

      {round && view.phase !== 'lobby' && view.phase !== 'game_over' && (
        <>
          <Scorebar players={view.players} />
          <TeamBar view={view} />
          <div className="row between" style={{ fontSize: 'clamp(1rem, 2vw, 2rem)', fontWeight: 800 }}>
            <span>
              Round {round.number}
              {roundStatus}
            </span>
            <Timer seconds={seconds} />
          </div>
          {round.worst && <WorstBanner stage="watch" />}
          {round.writtenBy && view.phase !== 'writing' && <span className="chip magenta tv-chip">✍️ Written by {round.writtenBy}</span>}
          {view.phase === 'choosing' ? (
            <div className="prompt-options">
              {(round.promptOptions ?? []).map((c) => (
                <div key={c.id} className="prompt-option waiting">
                  <PromptCard card={c} />
                </div>
              ))}
            </div>
          ) : view.phase === 'writing' ? (
            <div className="panel center writing-wait">
              <div className="pen" aria-hidden="true">
                ✍️
              </div>
              <h1 style={{ margin: 0 }}>{writer?.name ?? 'Someone'} is writing this round's prompt</h1>
            </div>
          ) : view.phase === 'guessing' ? (
            <>
              <div className="winner-banner pop-in" style={{ fontSize: 'clamp(1.2rem, 2.5vw, 2.5rem)' }}>
                🕵️ Who played this? Guess on your phones
              </div>
              <PromptCard card={round.prompt} answers={winners[0]?.cards?.map((c) => c.text)} />
              <p style={bigLine}>{guessWaiting.length ? `Waiting for ${names(guessWaiting)} to guess` : 'Everyone has guessed!'}</p>
            </>
          ) : view.phase === 'round_over' && winners[0]?.cards ? (
            <>
              <div className="winner-banner pop-in" style={{ fontSize: 'clamp(1.2rem, 2.5vw, 2.5rem)' }}>
                {round.worst ? '🥴 Sabse Bekaar: ' : '🏆 '}
                {winners.map((w) => w.playerName).join(' & ')}
              </div>
              <PromptCard card={round.prompt} answers={winners[0].cards.map((c) => c.text)} />
              {round.guesses && (
                <p style={bigLine}>
                  🕵️ {rightGuessers.length ? `${names(rightGuessers.map((g) => ({ name: g.playerName })))} guessed right!` : 'Nobody guessed it!'}
                </p>
              )}
            </>
          ) : (
            <PromptCard card={round.prompt} />
          )}
          {view.phase === 'submitting' && (
            <p style={{ fontSize: 'clamp(1.1rem, 2vw, 2rem)', fontWeight: 700, margin: 0 }}>
              {pending.length ? `Waiting for ${pending.map((p) => p.name).join(', ')}` : 'Everyone has played!'}
            </p>
          )}
          {view.phase === 'judging' && (
            <div className="submissions">
              {round.submissions.map((s) =>
                s.cards ? (
                  <div key={s.id} className="stack" style={{ gap: 8 }}>
                    {s.cards.map((c) => (
                      <AnswerCard key={c.id} card={c} className="flip-in" />
                    ))}
                  </div>
                ) : (
                  <FaceDownCard key={s.id} />
                ),
              )}
            </div>
          )}
          {view.phase === 'round_over' && !winners.length && <p className="notice">{round.notice}</p>}
        </>
      )}

      {view.phase === 'game_over' && (
        <>
          <h1 className="center">{champions.length ? `🏆 ${champions.join(' & ')} ${champions.length > 1 || view.teams.length ? 'win' : 'wins'}!` : 'Game over'}</h1>
          <TeamBar view={view} />
          <Scorebar players={[...view.players].sort((a, b) => b.score - a.score)} />
          <div className="stack">
            {view.recap.slice(0, 4).map((r) => (
              <div key={r.number} className="panel" style={{ fontSize: 'clamp(1rem, 1.8vw, 1.8rem)' }}>
                <FilledPrompt prompt={r.prompt} answers={r.winners[0]?.cards.map((c) => c.text) ?? []} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
